import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Truck, MapPin, Clock, User, Package, Camera, CheckCircle,
  CircleNotch, Warning, ArrowRight, WhatsappLogo, Buildings, Path,
  ListChecks, X, MapPinLine, XCircle, Prohibit, Sparkle
} from '@phosphor-icons/react';
import toast, { Toaster } from 'react-hot-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Estados con transiciones y labels visuales
const ESTADO_FLOW = [
  { key: 'planificado', label: 'Planificado', short: 'Viaje planificado' },
  { key: 'notificado', label: 'Llamado a andén', short: 'Llegar a planta' },
  { key: 'en_anden', label: 'En Andén', short: 'Llegué al andén' },
  { key: 'cargando', label: 'Cargando', short: 'Inicié la carga' },
  { key: 'en_transito', label: 'En Tránsito', short: 'Terminé de cargar' },
  { key: 'entregado', label: 'Entregado', short: 'Entregué la carga' },
  { key: 'completado', label: 'Completado', short: 'Finalizar viaje' },
];

const ESTADO_COLORS = {
  pendiente: 'bg-neutral-500',
  planificado: 'bg-cyan-500',
  notificado: 'bg-blue-500',
  en_anden: 'bg-amber-500',
  cargando: 'bg-orange-500',
  en_transito: 'bg-purple-500',
  entregado: 'bg-emerald-500',
  completado: 'bg-emerald-700',
  cancelado: 'bg-red-600',
};

const NEXT_ACTION_LABEL = {
  pendiente: { next: 'en_anden', label: '✓ Llegué al andén', icon: MapPin },
  planificado: { next: 'en_anden', label: '✓ Llegué al andén', icon: MapPin },
  notificado: { next: 'en_anden', label: '✓ Llegué al andén', icon: MapPin },
  en_anden: { next: 'cargando', label: '📦 Iniciar carga', icon: Package },
  cargando: { next: 'en_transito', label: '🚚 Iniciar transporte', icon: Truck },
  en_transito: { next: 'entregado', label: '✅ Entrega realizada', icon: CheckCircle },
  entregado: { next: 'completado', label: '🏁 Finalizar viaje', icon: CheckCircle },
};

export default function DriverPWA() {
  const { token } = useParams();
  const [shipment, setShipment] = useState(null);
  const [deliveries, setDeliveries] = useState([]);
  const [evidences, setEvidences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [error, setError] = useState(null);
  const [selectedDelivery, setSelectedDelivery] = useState(null);
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [deliveryStep, setDeliveryStep] = useState('choice');
  const [validationResult, setValidationResult] = useState(null);
  const [tipoEntrega, setTipoEntrega] = useState(null);
  const [confirmForm, setConfirmForm] = useState({ observaciones: '', cantidad_recibida: '', cantidad_rechazada: '' });
  const [validating, setValidating] = useState(false);
  const [lightboxFileId, setLightboxFileId] = useState(null);
  const fileInputRef = useRef(null);
  const guiaInputRef = useRef(null);
  const deliveryGuiaRef = useRef(null);
  const deliveryEvidenceRef = useRef(null);

  // Estados de verificación
  const [needsVerification, setNeedsVerification] = useState(false);
  const [phoneLastDigits, setPhoneLastDigits] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [verificationError, setVerificationError] = useState('');
  const [maskedPhone, setMaskedPhone] = useState('');

  const fetchShipment = async () => {
    try {
      const [shipRes, delRes, evRes] = await Promise.all([
        axios.get(`${API}/pwa/shipment/${token}`),
        axios.get(`${API}/pwa/shipment/${token}/deliveries`).catch(() => ({ data: [] })),
        axios.get(`${API}/pwa/shipment/${token}/evidences`).catch(() => ({ data: [] })),
      ]);
      setShipment(prev => {
        if (prev && shipRes.data && prev.estado !== shipRes.data.estado) {
          toast.success(`Estado actualizado: ${shipRes.data.estado.replace('_', ' ')}`, { duration: 3000 });
        }
        return shipRes.data;
      });
      setDeliveries(delRes.data);
      setEvidences(evRes.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Error cargando viaje');
    } finally {
      setLoading(false);
    }
  };

  const loadBasicInfo = async () => {
    try {
      const res = await axios.get(`${API}/pwa/shipment/${token}`);
      setMaskedPhone(res.data.conductor_telefono_masked || '****');
      setLoading(false);
    } catch (e) {
      setError('Token inválido o expirado');
      setLoading(false);
    }
  };

  const handleVerifyPhone = async () => {
    if (phoneLastDigits.length !== 4) {
      setVerificationError('Ingresa los 4 dígitos');
      return;
    }
    
    setVerifying(true);
    setVerificationError('');
    
    try {
      const res = await axios.post(`${API}/pwa/shipment/${token}/verify-phone`, {
        phone_last_digits: phoneLastDigits
      });
      
      localStorage.setItem(`pwa_verified_${token}`, res.data.session_token);
      setNeedsVerification(false);
      toast.success('Verificado correctamente');
      fetchShipment();
    } catch (err) {
      setVerificationError(err.response?.data?.detail || 'Código incorrecto');
    } finally {
      setVerifying(false);
    }
  };

  useEffect(() => {
    const verified = localStorage.getItem(`pwa_verified_${token}`);
    if (verified) {
      setNeedsVerification(false);
      fetchShipment();
    } else {
      setNeedsVerification(true);
      loadBasicInfo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (!needsVerification) {
      const interval = setInterval(fetchShipment, 10000);
      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, needsVerification]);

  const handleAdvanceStatus = async () => {
    const action = NEXT_ACTION_LABEL[shipment.estado];
    if (!action) return;
    
    if (!window.confirm(`¿Confirmar: ${action.label}?`)) return;
    
    setUpdating(true);
    try {
      await axios.post(`${API}/pwa/shipment/${token}/status`, {
        estado: action.next
      });
      toast.success('Estado actualizado');
      fetchShipment();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error actualizando');
    } finally {
      setUpdating(false);
    }
  };

  const handlePhotoUpload = async (file, tipo) => {
    if (!file) return;
    setUploadingPhoto(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      await axios.post(`${API}/pwa/shipment/${token}/evidence?tipo=${tipo}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(tipo === 'guia' ? '📄 Guía subida' : '📸 Foto subida');
      fetchShipment();
    } catch (e) {
      toast.error('Error subiendo foto');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const openDelivery = (d) => {
    setSelectedDelivery(d);
    setTipoEntrega(null);
    setValidationResult(null);
    setConfirmForm({ observaciones: '', cantidad_recibida: '', cantidad_rechazada: '' });
    setDeliveryStep('choice');
    setShowDeliveryModal(true);
  };

  const closeDelivery = () => {
    setShowDeliveryModal(false);
    setSelectedDelivery(null);
    setValidationResult(null);
  };

  const chooseTipoEntrega = (tipo) => {
    setTipoEntrega(tipo);
    if (tipo === 'no_entregado') {
      // Solo pedir observaciones
      setDeliveryStep('evidence');
    } else {
      // Total o parcial: pedir guía firmada primero (validación OCR)
      setDeliveryStep('validate_guia');
    }
  };

  const handleValidateGuia = async (file) => {
    if (!file || !selectedDelivery) return;
    setValidating(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(
        `${API}/pwa/deliveries/${selectedDelivery.id}/validate-guia?token=${token}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setValidationResult(res.data);
      if (res.data.match) {
        toast.success('✓ Guía validada correctamente');
      } else {
        toast.error('⚠ La guía NO corresponde a esta entrega');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error validando guía');
    } finally {
      setValidating(false);
    }
  };

  const handleDeliveryEvidence = async (file) => {
    if (!file || !selectedDelivery) return;
    setUploadingPhoto(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      await axios.post(
        `${API}/pwa/deliveries/${selectedDelivery.id}/evidence?token=${token}&tipo=foto_entrega`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success('📸 Evidencia subida');
      fetchShipment();
    } catch (e) {
      toast.error('Error subiendo evidencia');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleConfirmDelivery = async () => {
    if (!selectedDelivery || !tipoEntrega) return;
    setUpdating(true);
    try {
      await axios.post(
        `${API}/pwa/deliveries/${selectedDelivery.id}/confirm?token=${token}`,
        {
          tipo_entrega: tipoEntrega,
          cantidad_recibida: confirmForm.cantidad_recibida || null,
          cantidad_rechazada: confirmForm.cantidad_rechazada || null,
          observaciones: confirmForm.observaciones || null,
        }
      );
      toast.success('Entrega confirmada');
      closeDelivery();
      fetchShipment();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error confirmando');
    } finally {
      setUpdating(false);
    }
  };

  // Pantalla de verificación
  if (needsVerification) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-700 to-blue-900 flex items-center justify-center p-6">
        <Toaster position="top-center" />
        <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8">
          <div className="text-center mb-8">
            <div className="inline-block p-4 bg-blue-100 rounded-full mb-4">
              <WhatsappLogo size={48} className="text-blue-700" weight="fill" />
            </div>
            <h2 className="text-2xl font-bold text-neutral-900 mb-2">Verificación de Acceso</h2>
            <p className="text-neutral-600 text-sm">
              Por seguridad, confirma tu identidad
            </p>
          </div>

          <div className="mb-6">
            <p className="text-sm text-neutral-700 mb-4">
              Este enlace está asociado al número de WhatsApp:
            </p>
            <div className="bg-neutral-100 border border-neutral-200 rounded-lg p-4 text-center">
              <p className="text-2xl font-mono font-bold text-neutral-900">{maskedPhone}</p>
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Ingresa los últimos 4 dígitos de tu número
            </label>
            <input
              type="text"
              maxLength="4"
              value={phoneLastDigits}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '');
                setPhoneLastDigits(value);
                setVerificationError('');
              }}
              placeholder="1234"
              className={`w-full text-center text-2xl font-mono tracking-widest px-4 py-4 border-2 rounded-lg focus:outline-none focus:ring-2 ${
                verificationError
                  ? 'border-red-300 focus:ring-red-500 bg-red-50'
                  : 'border-neutral-300 focus:ring-blue-700'
              }`}
            />
            {verificationError && (
              <p className="mt-2 text-sm text-red-600 flex items-center">
                <XCircle size={16} className="mr-1" />
                {verificationError}
              </p>
            )}
          </div>

          <button
            onClick={handleVerifyPhone}
            disabled={verifying || phoneLastDigits.length !== 4}
            className={`w-full py-4 rounded-lg font-bold text-white transition-all ${
              verifying || phoneLastDigits.length !== 4
                ? 'bg-neutral-400 cursor-not-allowed'
                : 'bg-blue-700 hover:bg-blue-800 active:scale-95'
            }`}
          >
            {verifying ? (
              <span className="flex items-center justify-center">
                <CircleNotch size={20} className="animate-spin mr-2" />
                Verificando...
              </span>
            ) : (
              'Verificar y Continuar'
            )}
          </button>

          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-xs text-blue-800 flex items-start">
              <Prohibit size={16} className="mr-2 mt-0.5 flex-shrink-0" weight="fill" />
              <span>Solo el conductor asignado puede acceder a este viaje. Si no tienes acceso, contacta a tu coordinador.</span>
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center" data-testid="pwa-loading">
        <div className="text-center">
          <CircleNotch size={48} className="animate-spin text-blue-700 mx-auto" />
          <p className="mt-4 text-neutral-600 text-sm uppercase tracking-widest">Cargando viaje...</p>
        </div>
      </div>
    );
  }

  if (error || !shipment) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-6" data-testid="pwa-error">
        <div className="max-w-sm w-full bg-white border border-red-200 rounded-lg p-8 text-center">
          <Warning size={64} className="text-red-500 mx-auto mb-4" weight="fill" />
          <h2 className="text-xl font-bold text-neutral-900 mb-2">Viaje no encontrado</h2>
          <p className="text-neutral-600 text-sm">{error || 'El enlace puede haber expirado.'}</p>
        </div>
      </div>
    );
  }

  const currentAction = NEXT_ACTION_LABEL[shipment.estado];
  const isCompleted = shipment.estado === 'completado';
  const currentStepIndex = ESTADO_FLOW.findIndex(s => s.key === shipment.estado);

  return (
    <div className="min-h-screen bg-neutral-50 pb-24" data-testid="driver-pwa">
      <Toaster position="top-center" toastOptions={{
        style: { fontSize: '14px', padding: '12px 16px' }
      }} />

      {/* Header */}
      <header className="bg-neutral-950 text-white px-5 py-6 sticky top-0 z-40 shadow-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Truck size={28} weight="bold" className="text-blue-400" />
            <span className="ml-2 text-lg font-bold tracking-tight">SRoute</span>
          </div>
          {shipment.empresa_nombre && (
            <div className="flex items-center text-xs text-neutral-400">
              <Buildings size={14} className="mr-1" />
              {shipment.empresa_nombre}
            </div>
          )}
        </div>
        <div className="mt-4">
          <p className="text-xs uppercase tracking-[0.3em] text-blue-400 mb-1">Transporte</p>
          <h1 className="text-3xl font-bold tracking-tight font-mono">{shipment.numero_transporte}</h1>
        </div>
      </header>

      {/* Current Status */}
      <div className="px-5 pt-6" data-testid="current-status">
        <div className={`${ESTADO_COLORS[shipment.estado]} text-white rounded-lg p-6 shadow-lg`}>
          <p className="text-xs uppercase tracking-[0.3em] opacity-80 mb-2">Estado Actual</p>
          <p className="text-3xl font-bold tracking-tight uppercase">
            {ESTADO_FLOW.find(s => s.key === shipment.estado)?.label || shipment.estado}
          </p>
          {shipment.anden && (
            <div className="mt-4 pt-4 border-t border-white/20 flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] opacity-80">Andén asignado</p>
                <p className="text-2xl font-mono font-bold mt-1">{shipment.anden}</p>
              </div>
              <div className="text-right">
                <p className="text-xs uppercase tracking-[0.3em] opacity-80">Hora</p>
                <p className="text-2xl font-mono font-bold mt-1">{shipment.hora_carga}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Progress Timeline */}
      <div className="px-5 mt-6" data-testid="progress-timeline">
        <p className="text-xs uppercase tracking-[0.3em] text-neutral-500 mb-3 px-2">Progreso</p>
        <div className="bg-white rounded-lg p-4 border border-neutral-200">
          {ESTADO_FLOW.map((step, idx) => {
            const isDone = idx < currentStepIndex;
            const isCurrent = idx === currentStepIndex;
            const isPending = idx > currentStepIndex;
            return (
              <div key={step.key} className="flex items-center py-2">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                  isDone ? 'bg-emerald-500 text-white' :
                  isCurrent ? 'bg-blue-600 text-white ring-4 ring-blue-100' :
                  'bg-neutral-200 text-neutral-500'
                }`}>
                  {isDone ? <CheckCircle size={16} weight="fill" /> : <span className="text-xs font-bold">{idx + 1}</span>}
                </div>
                <div className="ml-3 flex-1">
                  <p className={`text-sm font-medium ${
                    isDone ? 'text-emerald-700' :
                    isCurrent ? 'text-blue-700' :
                    'text-neutral-400'
                  }`}>
                    {step.label}
                  </p>
                </div>
                {isCurrent && (
                  <span className="text-xs text-blue-700 font-medium uppercase tracking-wider animate-pulse">Ahora</span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Shipment Details */}
      <div className="px-5 mt-6" data-testid="shipment-details">
        <p className="text-xs uppercase tracking-[0.3em] text-neutral-500 mb-3 px-2">Guía de Despacho</p>
        <div className="bg-white rounded-lg border border-neutral-200 divide-y divide-neutral-100">
          <div className="p-4">
            <div className="flex items-start">
              <User size={18} className="text-neutral-500 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <p className="text-xs uppercase tracking-widest text-neutral-500">Conductor</p>
                <p className="text-sm font-medium text-neutral-900 mt-1">{shipment.conductor_nombre}</p>
              </div>
            </div>
          </div>
          <div className="p-4">
            <div className="flex items-start">
              <Truck size={18} className="text-neutral-500 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <p className="text-xs uppercase tracking-widest text-neutral-500">Vehículo</p>
                <p className="text-sm font-mono font-bold text-neutral-900 mt-1">{shipment.vehiculo_patente}</p>
              </div>
            </div>
          </div>
          <div className="p-4">
            <div className="flex items-start">
              <Path size={18} className="text-neutral-500 mt-0.5 mr-3 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-xs uppercase tracking-widest text-neutral-500">Ruta</p>
                <div className="mt-2 space-y-2">
                  <div className="flex items-center text-sm">
                    <span className="w-2 h-2 rounded-full bg-blue-600 mr-3"></span>
                    <span className="text-neutral-700">{shipment.origen}</span>
                  </div>
                  <div className="w-px h-4 bg-neutral-300 ml-1"></div>
                  <div className="flex items-center text-sm">
                    <span className="w-2 h-2 rounded-full bg-emerald-600 mr-3"></span>
                    <span className="font-medium text-neutral-900">{shipment.destino}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="p-4">
            <div className="flex items-start">
              <Clock size={18} className="text-neutral-500 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <p className="text-xs uppercase tracking-widest text-neutral-500">Fecha y hora de carga</p>
                <p className="text-sm font-medium text-neutral-900 mt-1">
                  {shipment.fecha_carga} · <span className="font-mono">{shipment.hora_carga}</span>
                </p>
              </div>
            </div>
          </div>
          {shipment.observaciones && (
            <div className="p-4 bg-amber-50">
              <div className="flex items-start">
                <Warning size={18} className="text-amber-600 mt-0.5 mr-3 flex-shrink-0" weight="fill" />
                <div>
                  <p className="text-xs uppercase tracking-widest text-amber-700">Observaciones</p>
                  <p className="text-sm text-neutral-800 mt-1">{shipment.observaciones}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Deliveries Section - only visible if there are multiple deliveries */}
      {deliveries.length > 0 && (
        <div className="px-5 mt-6" data-testid="deliveries-section">
          <p className="text-xs uppercase tracking-[0.3em] text-neutral-500 mb-3 px-2">
            Entregas ({deliveries.filter(d => d.estado === 'pendiente').length} pendientes)
          </p>
          <div className="space-y-2">
            {deliveries.map((d, idx) => {
              const isDone = d.estado !== 'pendiente';
              const badgeColor = {
                pendiente: 'bg-neutral-100 text-neutral-700',
                total: 'bg-emerald-100 text-emerald-800',
                parcial: 'bg-amber-100 text-amber-800',
                no_entregado: 'bg-red-100 text-red-800',
              }[d.estado] || 'bg-neutral-100 text-neutral-700';
              const deliveryPhotos = evidences.filter(e => e.delivery_id === d.id);
              return (
                <div
                  key={d.id}
                  className={`w-full bg-white border rounded-lg overflow-hidden ${
                    isDone ? 'border-neutral-200 opacity-90' : 'border-neutral-200 hover:border-blue-600 hover:shadow-md'
                  }`}
                  style={{ transition: 'border-color 0.2s ease, box-shadow 0.2s ease' }}
                  data-testid={`delivery-card-${d.id}`}
                >
                  <button
                    onClick={() => !isDone && openDelivery(d)}
                    disabled={isDone}
                    data-testid={`delivery-header-${d.id}`}
                    className="w-full text-left p-4 active:bg-blue-50 disabled:cursor-default"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 ${
                          isDone ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'
                        }`}>
                          {isDone ? <CheckCircle size={16} weight="fill" /> : <span className="text-xs font-bold">{d.orden || idx + 1}</span>}
                        </div>
                        <div>
                          <p className="text-xs uppercase tracking-widest text-neutral-500">
                            {d.numero_entrega}
                          </p>
                          <p className="text-sm font-medium text-neutral-900">{d.cliente_nombre}</p>
                        </div>
                      </div>
                      <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded ${badgeColor}`}>
                        {d.estado?.replace('_', ' ')}
                      </span>
                    </div>
                    <div className="text-xs text-neutral-600 flex items-start ml-11">
                      <MapPinLine size={12} className="mr-1 mt-0.5 flex-shrink-0" />
                      <span>{d.cliente_direccion}</span>
                    </div>
                    <div className="text-xs text-neutral-500 mt-1 ml-11 font-mono">
                      Guía: {d.numero_guia}
                    </div>
                  </button>
                  {deliveryPhotos.length > 0 && (
                    <div
                      className="px-4 pb-4 pt-1 border-t border-neutral-100 bg-neutral-50/40"
                      data-testid={`delivery-thumbs-${d.id}`}
                    >
                      <p className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2 mt-2">
                        {deliveryPhotos.length} evidencia{deliveryPhotos.length === 1 ? '' : 's'}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {deliveryPhotos.map(ev => (
                          <button
                            key={ev.id}
                            onClick={(e) => { e.stopPropagation(); setLightboxFileId(ev.id); }}
                            data-testid={`delivery-thumb-${ev.id}`}
                            title={ev.tipo}
                            className="relative w-16 h-16 rounded overflow-hidden border border-neutral-300 bg-neutral-100 hover:border-blue-600 active:opacity-70"
                          >
                            <img
                              src={`${API}/pwa/files/${ev.id}?token=${token}`}
                              alt={ev.tipo}
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                            <span className={`absolute top-0.5 left-0.5 text-[8px] uppercase tracking-widest px-1 rounded font-bold ${
                              ev.tipo === 'guia' ? 'bg-amber-500 text-white' :
                              ev.tipo === 'firma' ? 'bg-blue-600 text-white' :
                              ev.tipo === 'foto_entrega' ? 'bg-emerald-600 text-white' :
                              'bg-neutral-700 text-white'
                            }`}>
                              {ev.tipo === 'foto_entrega' ? 'POD' : ev.tipo}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Photo Upload Section */}
      <div className="px-5 mt-6" data-testid="photo-section">
        <p className="text-xs uppercase tracking-[0.3em] text-neutral-500 mb-3 px-2">Evidencias</p>
        <div className="grid grid-cols-2 gap-3">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={(e) => handlePhotoUpload(e.target.files[0], 'foto')}
            className="hidden"
          />
          <input
            ref={guiaInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={(e) => handlePhotoUpload(e.target.files[0], 'guia')}
            className="hidden"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadingPhoto}
            data-testid="upload-photo-btn"
            className="flex flex-col items-center justify-center p-4 bg-white border-2 border-neutral-200 rounded-lg hover:border-blue-600 hover:bg-blue-50 disabled:opacity-60 transition-colors"
          >
            <Camera size={32} className="text-blue-700 mb-2" weight="bold" />
            <span className="text-xs uppercase tracking-widest font-medium text-neutral-800">Foto POD</span>
          </button>
          <button
            onClick={() => guiaInputRef.current?.click()}
            disabled={uploadingPhoto}
            data-testid="upload-guia-btn"
            className="flex flex-col items-center justify-center p-4 bg-white border-2 border-neutral-200 rounded-lg hover:border-amber-600 hover:bg-amber-50 disabled:opacity-60 transition-colors"
          >
            <Package size={32} className="text-amber-700 mb-2" weight="bold" />
            <span className="text-xs uppercase tracking-widest font-medium text-neutral-800">Guía firmada</span>
          </button>
        </div>
        {uploadingPhoto && (
          <div className="mt-3 flex items-center justify-center text-sm text-neutral-600">
            <CircleNotch size={16} className="animate-spin mr-2" />
            Subiendo evidencia...
          </div>
        )}
        {/* General shipment thumbnails (evidences NOT linked to a specific delivery) */}
        {(() => {
          const generalEvs = evidences.filter(e => !e.delivery_id);
          if (generalEvs.length === 0) return null;
          return (
            <div className="mt-4" data-testid="general-thumbs">
              <p className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2 px-2">
                {generalEvs.length} foto{generalEvs.length === 1 ? '' : 's'} del viaje
              </p>
              <div className="flex flex-wrap gap-2 px-2">
                {generalEvs.map(ev => (
                  <button
                    key={ev.id}
                    onClick={() => setLightboxFileId(ev.id)}
                    data-testid={`general-thumb-${ev.id}`}
                    title={ev.tipo}
                    className="relative w-20 h-20 rounded overflow-hidden border border-neutral-300 bg-neutral-100 hover:border-blue-600 active:opacity-70"
                  >
                    <img
                      src={`${API}/pwa/files/${ev.id}?token=${token}`}
                      alt={ev.tipo}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    <span className={`absolute top-0.5 left-0.5 text-[9px] uppercase tracking-widest px-1 rounded font-bold ${
                      ev.tipo === 'guia' ? 'bg-amber-500 text-white' :
                      ev.tipo === 'firma' ? 'bg-blue-600 text-white' :
                      ev.tipo === 'foto_entrega' ? 'bg-emerald-600 text-white' :
                      'bg-neutral-700 text-white'
                    }`}>
                      {ev.tipo === 'foto_entrega' ? 'POD' : ev.tipo}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          );
        })()}
      </div>

      {/* Fixed Action Button */}
      {!isCompleted && currentAction && (
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-neutral-200 shadow-2xl" data-testid="action-bar">
          <button
            onClick={handleAdvanceStatus}
            disabled={updating}
            data-testid="advance-status-btn"
            className="w-full py-4 bg-blue-700 hover:bg-blue-800 disabled:opacity-60 text-white rounded-lg font-bold text-base uppercase tracking-wider flex items-center justify-center shadow-lg"
            style={{ transition: 'background-color 0.15s ease, transform 0.1s ease' }}
          >
            {updating ? (
              <><CircleNotch size={20} className="animate-spin mr-2" /> Actualizando...</>
            ) : (
              <>{currentAction.label} <ArrowRight size={20} className="ml-2" weight="bold" /></>
            )}
          </button>
        </div>
      )}

      {isCompleted && (
        <div className="px-5 mt-6" data-testid="completed-badge">
          <div className="bg-emerald-500 text-white rounded-lg p-8 text-center">
            <CheckCircle size={64} weight="fill" className="mx-auto mb-3" />
            <h2 className="text-2xl font-bold tracking-tight">¡Viaje Completado!</h2>
            <p className="mt-2 text-sm opacity-90">Gracias por tu trabajo.</p>
          </div>
        </div>
      )}

      {/* Delivery Confirmation Modal */}
      {showDeliveryModal && selectedDelivery && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center" data-testid="delivery-modal">
          <div className="bg-white w-full sm:max-w-lg sm:rounded-lg rounded-t-2xl max-h-[92vh] flex flex-col overflow-hidden">
            {/* Header */}
            <div className="p-5 border-b border-neutral-200 flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <p className="text-xs uppercase tracking-[0.3em] text-neutral-500 mb-1">
                  Entrega {selectedDelivery.numero_entrega}
                </p>
                <h2 className="text-lg font-bold text-neutral-900 truncate">
                  {selectedDelivery.cliente_nombre}
                </h2>
                <p className="text-xs text-neutral-600 mt-1">
                  <MapPinLine size={12} className="inline mr-1" />
                  {selectedDelivery.cliente_direccion}
                </p>
                <p className="text-xs text-neutral-500 mt-1 font-mono">
                  Guía esperada: <strong>{selectedDelivery.numero_guia}</strong>
                </p>
              </div>
              <button onClick={closeDelivery} data-testid="close-delivery-modal-btn" className="p-1 hover:bg-neutral-100 rounded ml-2">
                <X size={20} />
              </button>
            </div>

            {/* Step: Choice */}
            {deliveryStep === 'choice' && (
              <div className="p-5 space-y-3">
                <p className="text-sm text-neutral-700 mb-2">¿Cómo se realizó la entrega?</p>
                <button
                  onClick={() => chooseTipoEntrega('total')}
                  data-testid="choose-total-btn"
                  className="w-full p-4 bg-emerald-50 border-2 border-emerald-200 rounded-lg hover:bg-emerald-100 hover:border-emerald-500 text-left flex items-center"
                >
                  <CheckCircle size={32} weight="fill" className="text-emerald-600 mr-3" />
                  <div>
                    <p className="font-bold text-emerald-900">Entrega Total</p>
                    <p className="text-xs text-emerald-700">Cliente recibió todo</p>
                  </div>
                </button>
                <button
                  onClick={() => chooseTipoEntrega('parcial')}
                  data-testid="choose-parcial-btn"
                  className="w-full p-4 bg-amber-50 border-2 border-amber-200 rounded-lg hover:bg-amber-100 hover:border-amber-500 text-left flex items-center"
                >
                  <Warning size={32} weight="fill" className="text-amber-600 mr-3" />
                  <div>
                    <p className="font-bold text-amber-900">Entrega Parcial</p>
                    <p className="text-xs text-amber-700">Cliente rechazó parte de la mercadería</p>
                  </div>
                </button>
                <button
                  onClick={() => chooseTipoEntrega('no_entregado')}
                  data-testid="choose-no-entregado-btn"
                  className="w-full p-4 bg-red-50 border-2 border-red-200 rounded-lg hover:bg-red-100 hover:border-red-500 text-left flex items-center"
                >
                  <Prohibit size={32} weight="fill" className="text-red-600 mr-3" />
                  <div>
                    <p className="font-bold text-red-900">No Entregado</p>
                    <p className="text-xs text-red-700">No se pudo entregar (motivo requerido)</p>
                  </div>
                </button>
              </div>
            )}

            {/* Step: Validate Guia */}
            {deliveryStep === 'validate_guia' && (
              <div className="p-5 space-y-4 overflow-y-auto">
                <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                  <p className="text-xs text-blue-900 flex items-start">
                    <Sparkle size={14} weight="fill" className="mr-2 mt-0.5 flex-shrink-0" />
                    <span>Toma una foto de la guía firmada. La IA validará que corresponde a esta entrega.</span>
                  </p>
                </div>

                <input
                  ref={deliveryGuiaRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={(e) => handleValidateGuia(e.target.files[0])}
                  className="hidden"
                />
                <button
                  onClick={() => deliveryGuiaRef.current?.click()}
                  disabled={validating}
                  data-testid="capture-guia-btn"
                  className="w-full py-6 bg-amber-50 border-2 border-dashed border-amber-400 rounded-lg hover:bg-amber-100 disabled:opacity-60 flex flex-col items-center"
                >
                  {validating ? (
                    <>
                      <CircleNotch size={32} className="animate-spin text-amber-700 mb-2" />
                      <span className="text-sm font-medium text-amber-900">Validando con IA...</span>
                    </>
                  ) : (
                    <>
                      <Camera size={32} className="text-amber-700 mb-2" weight="bold" />
                      <span className="text-sm font-bold text-amber-900 uppercase tracking-widest">
                        Capturar guía firmada
                      </span>
                    </>
                  )}
                </button>

                {validationResult && (
                  <div className={`p-4 rounded-lg border ${
                    validationResult.match
                      ? 'bg-emerald-50 border-emerald-300'
                      : 'bg-red-50 border-red-300'
                  }`} data-testid="validation-result">
                    <div className="flex items-start">
                      {validationResult.match ? (
                        <CheckCircle size={24} weight="fill" className="text-emerald-600 mr-2 flex-shrink-0" />
                      ) : (
                        <XCircle size={24} weight="fill" className="text-red-600 mr-2 flex-shrink-0" />
                      )}
                      <div className="flex-1">
                        <p className={`font-bold text-sm ${
                          validationResult.match ? 'text-emerald-900' : 'text-red-900'
                        }`}>
                          {validationResult.match ? '✓ Guía validada' : '⚠ Guía no coincide'}
                        </p>
                        <p className="text-xs mt-1 text-neutral-700">
                          Esperado: <strong>{validationResult.numero_esperado}</strong>
                        </p>
                        <p className="text-xs text-neutral-700">
                          Detectado: <strong>{validationResult.numero_detectado || '—'}</strong>
                        </p>
                        {validationResult.confianza && (
                          <p className="text-xs text-neutral-500 mt-1">
                            Confianza IA: {validationResult.confianza}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex gap-2 pt-2">
                  <button
                    onClick={() => setDeliveryStep('choice')}
                    className="flex-1 py-3 bg-neutral-100 text-neutral-700 rounded-lg text-sm font-medium"
                  >
                    Atrás
                  </button>
                  <button
                    onClick={() => setDeliveryStep('evidence')}
                    disabled={!validationResult}
                    data-testid="continue-to-evidence-btn"
                    className="flex-1 py-3 bg-blue-700 text-white rounded-lg text-sm font-bold disabled:opacity-40"
                  >
                    Continuar
                  </button>
                </div>
              </div>
            )}

            {/* Step: Evidence + Confirm */}
            {deliveryStep === 'evidence' && (
              <div className="p-5 space-y-4 overflow-y-auto">
                {tipoEntrega !== 'no_entregado' && (
                  <>
                    <input
                      ref={deliveryEvidenceRef}
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={(e) => handleDeliveryEvidence(e.target.files[0])}
                      className="hidden"
                    />
                    <button
                      onClick={() => deliveryEvidenceRef.current?.click()}
                      disabled={uploadingPhoto}
                      data-testid="capture-evidence-btn"
                      className="w-full py-4 bg-blue-50 border-2 border-dashed border-blue-400 rounded-lg hover:bg-blue-100 disabled:opacity-60 flex items-center justify-center"
                    >
                      <Camera size={24} className="text-blue-700 mr-2" weight="bold" />
                      <span className="text-sm font-bold text-blue-900 uppercase tracking-widest">
                        {uploadingPhoto ? 'Subiendo...' : 'Foto de evidencia'}
                      </span>
                    </button>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs uppercase tracking-widest text-neutral-700 mb-1 font-medium">
                          Cant. recibida
                        </label>
                        <input
                          type="text"
                          value={confirmForm.cantidad_recibida}
                          onChange={(e) => setConfirmForm({ ...confirmForm, cantidad_recibida: e.target.value })}
                          data-testid="cantidad-recibida-input"
                          placeholder="ej: 10"
                          className="w-full px-3 py-2 border border-neutral-300 rounded text-sm"
                        />
                      </div>
                      {tipoEntrega === 'parcial' && (
                        <div>
                          <label className="block text-xs uppercase tracking-widest text-neutral-700 mb-1 font-medium">
                            Cant. rechazada
                          </label>
                          <input
                            type="text"
                            value={confirmForm.cantidad_rechazada}
                            onChange={(e) => setConfirmForm({ ...confirmForm, cantidad_rechazada: e.target.value })}
                            data-testid="cantidad-rechazada-input"
                            placeholder="ej: 2"
                            className="w-full px-3 py-2 border border-neutral-300 rounded text-sm"
                          />
                        </div>
                      )}
                    </div>
                  </>
                )}
                <div>
                  <label className="block text-xs uppercase tracking-widest text-neutral-700 mb-1 font-medium">
                    Observaciones {tipoEntrega === 'no_entregado' && '*'}
                  </label>
                  <textarea
                    value={confirmForm.observaciones}
                    onChange={(e) => setConfirmForm({ ...confirmForm, observaciones: e.target.value })}
                    data-testid="observaciones-input"
                    rows={3}
                    placeholder={tipoEntrega === 'no_entregado' ? 'Motivo de la no entrega (obligatorio)' : 'Notas adicionales...'}
                    className="w-full px-3 py-2 border border-neutral-300 rounded text-sm resize-none"
                  />
                </div>

                <div className="flex gap-2 pt-2 border-t border-neutral-100">
                  <button
                    onClick={() => setDeliveryStep(tipoEntrega === 'no_entregado' ? 'choice' : 'validate_guia')}
                    className="flex-1 py-3 bg-neutral-100 text-neutral-700 rounded-lg text-sm font-medium"
                  >
                    Atrás
                  </button>
                  <button
                    onClick={handleConfirmDelivery}
                    disabled={updating || (tipoEntrega === 'no_entregado' && !confirmForm.observaciones)}
                    data-testid="confirm-delivery-btn"
                    className={`flex-1 py-3 rounded-lg text-sm font-bold text-white disabled:opacity-40 ${
                      tipoEntrega === 'total' ? 'bg-emerald-600' :
                      tipoEntrega === 'parcial' ? 'bg-amber-600' :
                      'bg-red-600'
                    }`}
                  >
                    {updating ? 'Confirmando...' : `Confirmar ${tipoEntrega?.replace('_', ' ')}`}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightboxFileId && (
        <div
          className="fixed inset-0 bg-black/95 flex items-center justify-center z-[70] p-4"
          onClick={() => setLightboxFileId(null)}
          data-testid="pwa-lightbox"
        >
          <button
            onClick={() => setLightboxFileId(null)}
            data-testid="pwa-lightbox-close"
            className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white"
          >
            <X size={24} weight="bold" />
          </button>
          <img
            src={`${API}/pwa/files/${lightboxFileId}?token=${token}`}
            alt="Evidencia"
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
